/*
 * product-form.js — ATK Brasil
 *
 * - adiciona o produto ao carrinho da Shopify usando /cart/add.js;
 * - NÃO redireciona para /checkout (CartPanda assume só no checkout);
 * - atualiza cart-drawer/cart-notification quando disponível;
 * - funciona com variantes, quantidade e propriedades do produto.
 *
 * CORREÇÃO: inclui toggleSubmitButton() e handleErrorMessage(), que o
 * product-info.js do Dawn chama ao trocar o tamanho. Sem eles a troca de
 * tamanho travava e o carrinho recebia sempre o primeiro tamanho (P).
 */

if (!customElements.get('product-form')) {
  class ProductForm extends HTMLElement {
    constructor() {
      super();

      this.form = this.querySelector('form');
      if (!this.form) return;

      this.variantIdInput = this.form.querySelector('.product-variant-id');
      this.submitButton = this.form.querySelector('[type="submit"]');
      this.submitButtonText = this.submitButton ? (this.submitButton.querySelector('span') || this.submitButton) : null;
      this.errorWrapper = this.querySelector('[data-product-form-error]');
      this.errorMessage = this.querySelector('[data-product-form-error-message]');

      this.onSubmitHandler = this.onSubmitHandler.bind(this);
      this.form.addEventListener('submit', this.onSubmitHandler);
    }

    get cartAddUrl() {
      if (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) {
        return window.Shopify.routes.root + 'cart/add.js';
      }
      if (window.routes && window.routes.cart_add_url) {
        return window.routes.cart_add_url;
      }
      return '/cart/add.js';
    }

    get cartRootUrl() {
      if (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) {
        return window.Shopify.routes.root;
      }
      return '/';
    }

    /* ===== Métodos exigidos pelo Dawn (product-info.js) ===== */

    toggleSubmitButton(disable = true, text) {
      if (!this.submitButton) return;
      if (disable) {
        this.submitButton.setAttribute('disabled', 'disabled');
        if (text && this.submitButtonText) this.submitButtonText.textContent = text;
      } else {
        this.submitButton.removeAttribute('disabled');
        if (this.submitButtonText && window.variantStrings && window.variantStrings.addToCart) {
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }
    }

    handleErrorMessage(errorMessage = false) {
      if (errorMessage) {
        this.showError(errorMessage);
      } else {
        this.clearError();
      }
    }

    /* ======================================================== */

    setLoading(isLoading) {
      if (!this.submitButton) return;

      this.submitButton.disabled = isLoading;
      this.submitButton.classList.toggle('loading', isLoading);
      this.submitButton.setAttribute('aria-busy', isLoading ? 'true' : 'false');

      const spinner = this.submitButton.querySelector('.loading__spinner');
      if (spinner) spinner.classList.toggle('hidden', !isLoading);
    }

    showError(message) {
      const text = message || 'Não foi possível adicionar o produto ao carrinho.';

      if (this.errorMessage) this.errorMessage.textContent = text;
      if (this.errorWrapper) this.errorWrapper.hidden = false;

      const cartError = this.querySelector('.product-form__error-message');
      if (cartError) {
        cartError.textContent = text;
        cartError.removeAttribute('hidden');
      }

      console.error('ATK Brasil — carrinho:', text);
    }

    clearError() {
      if (this.errorWrapper) this.errorWrapper.hidden = true;

      const cartError = this.querySelector('.product-form__error-message');
      if (cartError) {
        cartError.textContent = '';
        cartError.setAttribute('hidden', 'hidden');
      }
    }

    async onSubmitHandler(event) {
      event.preventDefault();

      if (this.submitButton && this.submitButton.disabled) return;

      this.clearError();
      this.setLoading(true);

      let variantId = null;

      try {
        const formData = new FormData(this.form);
        variantId = formData.get('id');

        if (!variantId) {
          throw new Error('Selecione uma variação do produto antes de adicionar ao carrinho.');
        }

        formData.append('sections', 'cart-drawer,cart-icon-bubble');
        formData.append('sections_url', window.location.pathname + window.location.search);

        const response = await fetch(this.cartAddUrl, {
          method: 'POST',
          credentials: 'same-origin',
          headers: {
            Accept: 'application/json',
            'X-Requested-With': 'XMLHttpRequest'
          },
          body: formData
        });

        let result = null;
        try {
          result = await response.json();
        } catch (jsonError) {
          throw new Error('A loja retornou uma resposta inválida ao adicionar o produto.');
        }

        if (!response.ok) {
          throw new Error(
            (result && (result.description || result.message)) ||
              'Não foi possível adicionar o produto ao carrinho.'
          );
        }

        this.updateCartUI(result);

        if (typeof window.publish === 'function' && window.PUB_SUB_EVENTS && window.PUB_SUB_EVENTS.cartUpdate) {
          window.publish(window.PUB_SUB_EVENTS.cartUpdate, {
            source: 'product-form',
            productVariantId: Number(variantId),
            cartData: result
          });
        }

        document.dispatchEvent(
          new CustomEvent('atk:cart-added', {
            bubbles: true,
            detail: { variantId: Number(variantId), cart: result }
          })
        );
      } catch (error) {
        this.showError(error && error.message);
      } finally {
        this.setLoading(false);
      }
    }

    updateCartUI(result) {
      const cartDrawer = document.querySelector('cart-drawer');
      const cartNotification = document.querySelector('cart-notification');

      // CORREÇÃO: quando o carrinho estava vazio, a gaveta continuava marcada
      // como "vazia" e escondia os produtos (tela branca). Removemos essa marca.
      if (cartDrawer) cartDrawer.classList.remove('is-empty');
      if (cartNotification) cartNotification.classList.remove('is-empty');

      if (cartDrawer && typeof cartDrawer.renderContents === 'function') {
        if (result && result.sections) {
          cartDrawer.renderContents(result);
        } else if (typeof cartDrawer.open === 'function') {
          cartDrawer.open();
        }
        return;
      }

      if (cartNotification && typeof cartNotification.renderContents === 'function' && result && result.sections) {
        cartNotification.renderContents(result);
        return;
      }

      this.refreshCartFallback();
    }

    async refreshCartFallback() {
      try {
        const sectionsUrl = this.cartRootUrl + 'cart?sections=cart-drawer,cart-icon-bubble';
        const response = await fetch(sectionsUrl, {
          credentials: 'same-origin',
          headers: { Accept: 'application/json' }
        });
        if (!response.ok) return;

        const state = await response.json();
        const cartDrawer = document.querySelector('cart-drawer');

        if (cartDrawer && typeof cartDrawer.renderContents === 'function') {
          cartDrawer.classList.remove('is-empty');
          cartDrawer.renderContents(state);
          if (typeof cartDrawer.open === 'function') cartDrawer.open();
          return;
        }

        const cartIcon = document.querySelector('#cart-icon-bubble');
        if (cartIcon && state['cart-icon-bubble']) {
          const doc = new DOMParser().parseFromString(state['cart-icon-bubble'], 'text/html');
          const replacement = doc.querySelector('#cart-icon-bubble');
          if (replacement) cartIcon.replaceWith(replacement);
        }
      } catch (error) {
        console.warn('ATK Brasil — atualização visual do carrinho:', error);
      }
    }
  }

  customElements.define('product-form', ProductForm);
}